import { seal, unseal, type VaultKey } from './crypto';
import {
  isEffectivelyFinished,
  type Bookmark,
  type Collection,
  type Favorite,
  type HistoryEntry,
  type MediaItem,
  type ProfileId,
  type ResumePoint,
  type StoreName,
  type StoredRecord,
} from './entities';
import { openRecordStore } from './indexedDbStore';
import type { RecordStore } from './recordStore';

/**
 * Profile-scoped storage.
 *
 * A `ProfileStorage` is bound to exactly one profile at construction and has
 * no method that takes a profile argument. There is deliberately no way to ask
 * it for another profile's data, because the safest version of "private items
 * must never appear in shared lists" is one where the shared code has no
 * expressible way to request them.
 *
 * Underneath, each profile is a separate database. The isolation is not a
 * filter that a future query could forget to apply.
 */

/** Reads and writes one kind of record. */
export interface Repository<T extends StoredRecord> {
  get(id: string): Promise<T | null>;
  put(record: T): Promise<void>;
  delete(id: string): Promise<void>;
  all(): Promise<readonly T[]>;
  clear(): Promise<void>;
}

/**
 * Converts records to and from the form written to disk.
 *
 * For the shared profile this is the identity. For private it seals and
 * unseals, which is where encryption at rest actually happens.
 */
interface Codec {
  encode(value: unknown): Promise<unknown>;
  decode(stored: unknown): Promise<unknown>;
}

const IDENTITY_CODEC: Codec = {
  async encode(value) {
    return value;
  },
  async decode(stored) {
    return stored;
  },
};

function sealingCodec(key: VaultKey): Codec {
  return {
    encode: (value) => seal(key, value),
    decode: async (stored) => {
      if (stored === undefined || stored === null) return null;
      // A record that fails to decrypt is treated as absent rather than
      // throwing. One damaged row should not make an entire library
      // unreadable, and the alternative to skipping it is a screen that
      // cannot render at all.
      return unseal(key, stored as Awaited<ReturnType<typeof seal>>);
    },
  };
}

class StoreRepository<T extends StoredRecord> implements Repository<T> {
  constructor(
    private readonly store: RecordStore,
    private readonly name: StoreName,
    private readonly codec: Codec,
  ) {}

  async get(id: string): Promise<T | null> {
    const stored = await this.store.get(this.name, id);
    if (stored === undefined || stored === null) return null;
    return ((await this.codec.decode(stored)) as T | null) ?? null;
  }

  async put(record: T): Promise<void> {
    await this.store.put(this.name, record.id, await this.codec.encode(record));
  }

  async delete(id: string): Promise<void> {
    await this.store.delete(this.name, id);
  }

  async all(): Promise<readonly T[]> {
    const rows = await this.store.all(this.name);
    const decoded = await Promise.all(rows.map((row) => this.codec.decode(row.value)));
    // Rows that failed to decrypt drop out here rather than surfacing as null.
    return decoded.filter((value): value is T => value !== null && value !== undefined);
  }

  async clear(): Promise<void> {
    await this.store.clear(this.name);
  }
}

export interface ProfileStorage {
  readonly profile: ProfileId;
  /** False when IndexedDB was unavailable and nothing will outlive the session. */
  readonly persistent: boolean;
  readonly encrypted: boolean;

  readonly media: Repository<MediaItem>;
  readonly history: Repository<HistoryEntry>;
  readonly resume: Repository<ResumePoint>;
  readonly favorites: Repository<Favorite>;
  readonly bookmarks: Repository<Bookmark>;
  readonly collections: Repository<Collection>;

  /** Everything still in progress, most recently watched first. */
  continueWatching(limit?: number): Promise<readonly ContinueWatchingItem[]>;
  /** Record where a film has got to. Marks it finished near the end. */
  recordProgress(mediaId: string, positionMs: number, durationMs: number | null): Promise<void>;

  close(): void;
}

export interface ContinueWatchingItem {
  readonly media: MediaItem;
  readonly resume: ResumePoint;
  readonly progress: number;
}

/**
 * Open storage for one profile.
 *
 * A vault key is required for the private profile and rejected for the shared
 * one. Enforcing that here rather than trusting callers means private data
 * cannot be written unencrypted by mistake.
 */
export async function openProfileStorage(
  profile: ProfileId,
  vaultKey: VaultKey | null = null,
): Promise<ProfileStorage> {
  if (profile === 'private' && vaultKey === null) {
    throw new Error('Private viewing storage cannot be opened without an unlocked vault');
  }
  if (profile === 'shared' && vaultKey !== null) {
    throw new Error('Shared storage does not take a vault key');
  }

  const { store, persistent } = await openRecordStore(profile);
  const codec = vaultKey === null ? IDENTITY_CODEC : sealingCodec(vaultKey);

  return buildStorage(profile, store, codec, persistent, vaultKey !== null);
}

/** Construct storage over a supplied backend. Used directly by the tests. */
export function buildStorage(
  profile: ProfileId,
  store: RecordStore,
  codec: Codec,
  persistent: boolean,
  encrypted: boolean,
): ProfileStorage {
  const media = new StoreRepository<MediaItem>(store, 'media', codec);
  const resume = new StoreRepository<ResumePoint>(store, 'resume', codec);

  return {
    profile,
    persistent,
    encrypted,
    media,
    resume,
    history: new StoreRepository<HistoryEntry>(store, 'history', codec),
    favorites: new StoreRepository<Favorite>(store, 'favorites', codec),
    bookmarks: new StoreRepository<Bookmark>(store, 'bookmarks', codec),
    collections: new StoreRepository<Collection>(store, 'collections', codec),

    async continueWatching(limit = 20) {
      const points = await resume.all();
      const unfinished = points.filter((point) => !point.finished);
      if (unfinished.length === 0) return [];

      const items = await Promise.all(
        unfinished.map(async (point) => {
          const item = await media.get(point.id);
          if (item === null) return null;
          return {
            media: item,
            resume: point,
            progress:
              point.durationMs !== null && point.durationMs > 0
                ? Math.min(1, point.positionMs / point.durationMs)
                : 0,
          } satisfies ContinueWatchingItem;
        }),
      );

      return items
        .filter((item): item is ContinueWatchingItem => item !== null)
        .sort((a, b) => b.resume.updatedAt - a.resume.updatedAt)
        .slice(0, limit);
    },

    async recordProgress(mediaId, positionMs, durationMs) {
      await resume.put({
        id: mediaId,
        positionMs,
        durationMs,
        finished: isEffectivelyFinished(positionMs, durationMs),
        updatedAt: Date.now(),
      });
    },

    close() {
      store.close();
    },
  };
}

export { IDENTITY_CODEC, sealingCodec };
export type { Codec };
